import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaChevronDown,
  FaEnvelope,
  FaFacebookF,
  FaGlobe,
  FaInstagram,
  FaPlus,
  FaTimes,
} from "react-icons/fa";
import "./HubOrgs.css";

const API_BASE = (
  process.env.REACT_APP_GOOURHUB_BUSINESS_API ||
  "https://business-api.goourhub.com"
).replace(/\/+$/, "");

/* =========================================================
   HubOrgs   "Nonprofits & community orgs" on the hub.

   • Cards: logo (CDN img/orgs/<id>.png, initials fallback),
     org name, community chip, one-line mission, and
     website / Facebook / Instagram / email buttons.
   • Tapping a card opens the org detail sheet: logo, about,
     links, optional volunteer link, and a gold "Post your
     event" button (fires gohub:post-event — the same
     sign-in-gated flow HubQuickActions handles).
   • Desktop: two rows + "View all N". Phones: swipe rail.

   Events are deliberately NOT shown per org: the events API has
   no "posted by org" field, so any automatic match would pin
   someone else's event on an organization. When events carry an
   org id, chips can come back with honest attribution.

   Data: GET /api/organizations (MongoDB-backed). Section header styles: .hub-sechead
   in Hub.css. Place on the hub with:  <HubOrgs />
   ========================================================= */

const LOGO_BASE = "https://goourhub.sfo2.digitaloceanspaces.com/img/orgs/";

const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const initials = (name) =>
  String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

const logoUrl = (org) =>
  org.logo || `${LOGO_BASE}${org.slug || slugify(org.name)}.png`;

function OrgLogo({ org, size = "" }) {
  const [failed, setFailed] = useState(false);
  const src = logoUrl(org);

  return (
    <span className={`horg-logo ${size}`} aria-hidden="true">
      {failed || !src ? (
        initials(org.name)
      ) : (
        <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
      )}
    </span>
  );
}

function OrgLinks({ org, stop, labeled = false }) {
  const links = [
    {
      key: "website",
      href: org.website,
      icon: FaGlobe,
      label: "Website",
      external: true,
    },
    {
      key: "facebook",
      href: org.facebook,
      icon: FaFacebookF,
      label: "Facebook",
      external: true,
    },
    {
      key: "instagram",
      href: org.instagram,
      icon: FaInstagram,
      label: "Instagram",
      external: true,
    },
    {
      key: "email",
      href: org.email ? `mailto:${org.email}` : "",
      icon: FaEnvelope,
      label: "Email",
      external: false,
    },
  ].filter((link) => link.href);

  if (links.length === 0) return null;

  return (
    <span className="horg-links">
      {links.map((link) => {
        const Icon = link.icon;

        /* the card shows WEBSITE as a labeled pill (so it reads as a
           website link at a glance); FB / IG / email stay recognizable
           brand icons. The detail sheet labels everything. */
        const showLabel = labeled || link.key === "website";

        return (
          <a
            key={link.key}
            className={`horg-soc${showLabel ? " horg-soc--pill" : ""}`}
            href={link.href}
            {...(link.external
              ? {
                  target: "_blank",
                  rel: "noopener noreferrer",
                }
              : {})}
            aria-label={
              link.key === "email"
                ? `Email ${org.name}`
                : `${org.name} on ${link.label}`
            }
            title={link.key === "email" ? org.email : link.label}
            onClick={stop}
          >
            <Icon />
            {showLabel && <span>{link.label}</span>}
          </a>
        );
      })}
    </span>
  );
}

/* the big card: logo, about, links, volunteer, post CTA */
function OrgSheet({ org, onClose }) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="horg-sheet-wrap"
      role="dialog"
      aria-modal="true"
      aria-label={org.name}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="horg-sheet">
        <button type="button" className="horg-sheet__close" aria-label="Close" onClick={onClose}>
          <FaTimes />
        </button>

        <div className="horg-sheet__head">
          <OrgLogo org={org} size="horg-logo--lg" />
          <div>
            <h3>{org.name}</h3>
            {org.communityLabel && <span className="horg-chip">{org.communityLabel}</span>}
          </div>
        </div>

        {org.about && <p className="horg-sheet__about">{org.about}</p>}

        <OrgLinks org={org} labeled />

        {org.volunteer && (
          <a
            className="horg-sheet__volunteer"
            href={org.volunteer}
            target="_blank"
            rel="noopener noreferrer"
          >
            Volunteer with {org.communityLabel || "this org"} →
          </a>
        )}

        <button
          type="button"
          className="horg-sheet__post"
          onClick={() => {
            onClose();
            window.dispatchEvent(new CustomEvent("gohub:post-event"));
          }}
        >
          <FaPlus aria-hidden="true" /> Post your event
          <small>Free for nonprofits</small>
        </button>
      </div>
    </div>,
    document.body
  );
}

/* cards per desktop row (HubOrgs.css: 4 wide, 3 on tablets, rail on
   phones where 0 means "phone preview") */
function getOrgsPerRow() {
  if (typeof window === "undefined") return 4;
  if (window.matchMedia("(max-width: 720px)").matches) return 0;
  if (window.matchMedia("(max-width: 1050px)").matches) return 3;
  return 4;
}

const PHONE_PREVIEW = 12;

export default function HubOrgs() {
  const [activeOrg, setActiveOrg] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [perRow, setPerRow] = useState(() => getOrgsPerRow());
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(true);
  const [organizationsError, setOrganizationsError] = useState("");

  useEffect(() => {
    const openFromHubSearch = (event) => {
      const selected = event?.detail?.organization;
      if (selected && typeof selected === "object") setActiveOrg(selected);
    };

    window.addEventListener("gohub:open-organization", openFromHubSearch);
    return () =>
      window.removeEventListener("gohub:open-organization", openFromHubSearch);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadOrganizations = async () => {
      setLoadingOrganizations(true);
      setOrganizationsError("");

      try {
        const response = await fetch(`${API_BASE}/api/organizations`, {
          method: "GET",
          signal: controller.signal,
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data.message || "Could not load nonprofit organizations."
          );
        }

        setOrganizations(
          Array.isArray(data.organizations) ? data.organizations : []
        );
      } catch (error) {
        if (error?.name === "AbortError") return;

        console.error("Could not load organizations:", error);
        setOrganizations([]);
        setOrganizationsError(
          error?.message || "Could not load nonprofit organizations."
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoadingOrganizations(false);
        }
      }
    };

    loadOrganizations();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const queries = [
      window.matchMedia("(max-width: 720px)"),
      window.matchMedia("(max-width: 1050px)"),
    ];

    const onChange = () => setPerRow(getOrgsPerRow());

    queries.forEach((query) => {
      if (query.addEventListener) query.addEventListener("change", onChange);
      else query.addListener(onChange);
    });

    return () => {
      queries.forEach((query) => {
        if (query.removeEventListener) {
          query.removeEventListener("change", onChange);
        } else {
          query.removeListener(onChange);
        }
      });
    };
  }, []);

  if (loadingOrganizations) {
    return (
      <section className="horg" aria-labelledby="horg-title">
        <div className="hub-sechead">
          <span className="hub-sechead__ic" aria-hidden="true">
            💜
          </span>

          <div className="hub-sechead__tx">
            <h2 id="horg-title">Nonprofits &amp; community orgs</h2>
            <p>Loading local organizations…</p>
          </div>
        </div>
      </section>
    );
  }

  if (organizationsError) {
    return (
      <section className="horg" aria-labelledby="horg-title">
        <div className="hub-sechead">
          <span className="hub-sechead__ic" aria-hidden="true">
            💜
          </span>

          <div className="hub-sechead__tx">
            <h2 id="horg-title">Nonprofits &amp; community orgs</h2>
            <p>Organizations are temporarily unavailable. Please try again shortly.</p>
          </div>
        </div>
      </section>
    );
  }

  if (organizations.length === 0) return null;

  const preview = perRow === 0 ? PHONE_PREVIEW : perRow * 2; /* two rows */
  const collapsible = organizations.length > preview;
  const shownOrgs =
    collapsible && !expanded ? organizations.slice(0, preview) : organizations;

  return (
    <section
      className={`horg ${expanded ? "is-expanded" : ""}`}
      aria-labelledby="horg-title"
    >
      <div className="hub-sechead">
        <span className="hub-sechead__ic" aria-hidden="true">
          💜
        </span>

        <div className="hub-sechead__tx">
          <h2 id="horg-title">
            Nonprofits &amp; community orgs
            <span className="hub-sechead__count">{organizations.length}</span>
          </h2>
          <p>Local organizations doing the work. Follow, join, show up.</p>
        </div>

        {collapsible && (
          <button
            type="button"
            className="hub-sechead__all horg-all"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? "Show less" : "View all"}
          </button>
        )}
      </div>

      <div className="horg-rail">
        {shownOrgs.map((org) => {
          return (
            <article
              key={org.id || org.slug || org.name}
              className={`horg-card${org.featured ? " horg-card--feat" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => setActiveOrg(org)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setActiveOrg(org);
                }
              }}
            >
              {org.featured && (
                <span className="horg-feat-tag">Org of the month</span>
              )}

              <div className="horg-card__top">
                <OrgLogo org={org} />
                <div className="horg-card__name">
                  <b>{org.name}</b>
                  {org.communityLabel && (
                    <span className="horg-chip">{org.communityLabel}</span>
                  )}
                </div>
              </div>

              {org.about && (
                <p className="horg-card__about">{org.about}</p>
              )}

              <div className="horg-card__foot">
                <OrgLinks org={org} stop={(event) => event.stopPropagation()} />
              </div>
            </article>
          );
        })}
      </div>

      {/* desktop fade + bar, same pattern as communities/categories */}
      {collapsible && (
        <div className="hub-more-wrap horg-more">
          <button
            type="button"
            className="hub-more"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? "Show fewer organizations" : `View all ${organizations.length} organizations`}
            <FaChevronDown aria-hidden="true" />
          </button>
        </div>
      )}

      <Link to="/contact" className="horg-join">
        <span aria-hidden="true">🏛️</span>
        <span>
          Run a nonprofit? <b>Get listed free</b> — links, socials &amp; events.
        </span>
        <FaArrowRight className="horg-join__go" aria-hidden="true" />
      </Link>

      {activeOrg && (
        <OrgSheet org={activeOrg} onClose={() => setActiveOrg(null)} />
      )}
    </section>
  );
}
